import { SatelliteFlight } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateSatelliteFlightArgs,
    getDefaultSatelliteFlightCreateArgs,
    UpdateSatelliteFlightArgs,
} from "@nova/serializer/src/client/validators/flights/satellite"
import { selectSatelliteFlights } from "features/entity-caching/flights/selectors"
import {
    addEntitySocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { v4 as randomUUID } from "uuid"
import { setFlightId } from "features/map/control/slice"
import {
    selectSingleLayer,
    setActiveFeatureId,
    setActiveLayerId,
} from "panels/content/side-panel/slice.new"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"

const {
    create: createSatelliteFlight,
    update: updateSatelliteFlight,
    delete: deleteSatelliteFlight,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    SatelliteFlight, // entity type
    CreateSatelliteFlightArgs, // create args to send to server
    [
        "createdBy",
        "region",
        "sensorType",
        "temporalAnchor",
        "name",
        "cloudFree",
        "dehaze",
        "temporalBias",
        "parentId",
    ], // create args required by action
    UpdateSatelliteFlightArgs, // update args
    { temporalBias?: string; orderOffset?: number } // context
>("SatelliteFlight")

export const addSatelliteFlightsToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const state = store.getState()
            const flights = selectSatelliteFlights(state)
            const name = `Satellite ${Object.values(flights).length + 1}`
            const id = randomUUID()
            const args = { name, id }
            const optimisticData = {
                ...args,
                ...getDefaultSatelliteFlightCreateArgs(
                    state.project.data,
                    data.parentId,
                    context?.orderOffset
                ),
                ...data,
            }
            createSatelliteFlight(socket, { ...args, ...data }, context ?? {})
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["satelliteFlights"],
                })
            )

            store.dispatch(selectSingleLayer({ id: optimisticData.id }))
            store.dispatch(setFlightId(id))
            store.dispatch(setActiveLayerId({ id: optimisticData.id }))
            store.dispatch(setActiveFeatureId({ feature: NovaPluginIds.Satellite }))
        })
        .addCase(updateAction, (store, { payload: { id, updates } }, socket) => {
            updateSatelliteFlight(socket, id, updates, {})
            store.dispatch(updateEntitySocket({ updates, path: ["satelliteFlights", { id }] }))
        })
        .addCase(deleteAction, (store, { payload: { id } }, socket) => {
            const dispatch = store.dispatch
            deleteSatelliteFlight(socket, id, {})
            dispatch(deleteEntitySocket({ path: ["satelliteFlights", { id }] }))
        })
export {
    createAction as createSatelliteFlight,
    updateAction as updateSatelliteFlight,
    deleteAction as deleteSatelliteFlight,
}
